import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useFeatureFlags } from 'libs.flags.react';
import { Button, useLocalizeMessage, Spinner, Table } from 'libs.nucleus';
import { SectionCard } from 'libs.react';
import { ParticipantDocumentModal } from './participant_document_modal.component';
import { buildDocumentUrl } from './participant_documents.utils';
import { useParticipantDocuments, useParticipantDocumentsTableColumns, } from './participants_documents_table_columns.hook';
import { useCreateConsentAction } from './use_create_consent_action.hook';
import { usePtsDocumentAction } from './use_pts_document_action.hook';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { isParticipantDeactivated, isParticipantInactive } from '../../helpers/participants.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
export const ParticipantDocuments = ({ participant }) => {
    const translate = useLocalizeMessage();
    const [selectedDocument, setSelectedDocument] = useState(null);
    const { isUserPermitted } = useLumosAuth();
    const { environment } = useCurrentEnvironment();
    const { isFeatureEnabled } = useFeatureFlags();
    const isDocGenEconsentEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.DOC_GEN_ECONSENT);
    const getDocumentHref = useCallback((document) => {
        return buildDocumentUrl(document._id, environment) || '';
    }, [environment]);
    const openDocument = useCallback((document) => {
        setSelectedDocument(document);
    }, []);
    const closeDocumentModal = useCallback(() => {
        setSelectedDocument(null);
    }, []);
    const { isPtsInteractionEnabled, openPtsDocument, ptsDocumentModal } = usePtsDocumentAction(participant);
    const { participantDocumentsColumns } = useParticipantDocumentsTableColumns({
        getDocumentHref,
        openDocumentsInModal: isDocGenEconsentEnabled,
        onOpenDocument: openDocument,
        isPtsInteractionEnabled,
        onOpenPtsDocument: openPtsDocument,
    });
    const { data: documents, isLoading } = useParticipantDocuments(participant);
    const canCreateDocument = !!participant &&
        !isParticipantDeactivated(participant) &&
        !isParticipantInactive(participant) &&
        isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_CREATE_CONSENTS);
    const { handleCreateConsent, createConsentModal } = useCreateConsentAction(participant, canCreateDocument);
    if (isLoading || !participant) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    if (isEmpty(documents)) {
        return (_jsxs(_Fragment, { children: [_jsx(SectionCard, { children: _jsxs("section", { className: 'flex flex-col items-center justify-center gap-8 p-10', children: [_jsx("p", { className: 'text-sm text-secondary', children: translate('info.participant_documents.no_consents') }), canCreateDocument && (_jsx(Button, { label: translate('action.participant_documents.create_consent'), variant: 'secondary', onClick: handleCreateConsent }))] }) }), createConsentModal, ptsDocumentModal] }));
    }
    return (_jsxs("div", { className: 'flex flex-col gap-4', children: [canCreateDocument && (_jsx("div", { className: 'flex justify-end', children: _jsx(Button, { label: translate('action.participant_documents.create_consent'), variant: 'secondary', onClick: handleCreateConsent }) })), _jsx(Table, { columns: participantDocumentsColumns, data: documents || [], hasPagination: true, initialPageSize: 10, isLoading: isLoading, labelNoResults: translate('label.participant_documents.empty'), paginationNavigationScreenReaderLabel: translate('info.participant_documents.table_navigation') }), createConsentModal, ptsDocumentModal, isDocGenEconsentEnabled && (_jsx(ParticipantDocumentModal, { document: selectedDocument, isOpen: !!selectedDocument, onClose: closeDocumentModal }))] }));
};
